"use client";

import * as yup from "yup";
import Link from "next/link";
import { Button } from "../button";
import { getImageURL, getAbsoluteURL } from "@/lib";
import { useForm } from "react-hook-form";
import { useCommonContext } from "@/contexts";
import { API_ENDPOINTS } from "@/config/endpoints";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect, useCallback } from "react";
import { POST_REQUEST, cleanSEOTags } from "@/utilities";
import { Collapse, Fade } from "@/components/transitions";
import { getCookie, setCookie } from "@/utilities/set-lang";
import { useRouter, useSearchParams } from "next/navigation";

export const CommonHeader = ({ data = {} }) => {
    const { logo, menu, button, ["secondary-menu"]: secondaryMenu } = data;
    const [levelActive, setLevelActive] = useState({ 1: "", 2: "", 3: "" });

    const { is1200 } = useCommonContext();

    const searchParams = useSearchParams();
    const methods = useForm({
        defaultValues: {
            search: searchParams?.get("search") || "",
        },
        resolver: yupResolver(
            yup.object().shape({
                search: yup.string().min(3).required(),
            }),
        ),
    });

    const router = useRouter();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [currentLang, setCurrentLang] = useState(null);
    const [searchActive, setSearchActive] = useState(false);

    // Search Suggestions State
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [redirecting, setRedirecting] = useState(false);

    // Recent Searches State
    const [recentSearches, setRecentSearches] = useState([]);
    const [showRecentSearches, setShowRecentSearches] = useState(false);

    const toggleMenu = () => setMenuOpen((prev) => !prev);
    const closeMenu = () => setMenuOpen(false);

    const getLanguage = async () => {
        setCurrentLang(await handleActionClick("get_lang"));
    };

    const activeInactiveMenu = (index, title) => {
        console.log(index, title, "index");
        setLevelActive((prevState) => {
            let data = { ...prevState };

            // set clicked level
            if (data[index] != title) {
                data[index] = title;
            } else {
                data[index] = "";
            }

            // reset all next levels
            const totalLevels = Object.keys(prevState).length;
            for (let i = index + 1; i <= totalLevels; i++) {
                data[i] = "";
            }

            console.log(data, "updated state after loop");
            return data;
        });
    };

    const handleActionClick = async (action = "", value = "") => {
        switch (action) {
            case "get_lang":
                return (await getCookie("lang"))?.value;

            case "lang_change":
                const newLang = currentLang === "en" ? "ar" : "en";

                await setCookie("lang", newLang);
                setCurrentLang(newLang);
                router.refresh();

                break;
        }
    };

    useEffect(() => {
        if (typeof window !== "undefined") {
            const handleScroll = () => {
                const scrollPosition = window.scrollY;
                setIsScrolled(scrollPosition > 50);
            };

            window.addEventListener("scroll", handleScroll);

            return () => {
                window.removeEventListener("scroll", handleScroll);
            };
        }
    }, []);

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.body.style.overflow = menuOpen ? "hidden" : "inherit";
        }
    }, [menuOpen]);

    useEffect(() => {
        getLanguage();
        setRecentSearches(getRecentSearches());
    }, []);

    const getRecentSearches = () => {
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("recentSearches");
            return saved ? JSON.parse(saved) : [];
        }
        return [];
    };

    const saveRecentSearch = (searchTerm) => {
        if (!searchTerm || searchTerm.trim().length === 0) return;

        const currentRecent = getRecentSearches();
        const filtered = currentRecent.filter(
            (item) => item.toLowerCase() !== searchTerm.toLowerCase(),
        );
        const updated = [searchTerm, ...filtered].slice(0, 10);

        localStorage.setItem("recentSearches", JSON.stringify(updated));
        setRecentSearches(updated);
    };

    const clearRecentSearches = () => {
        localStorage.removeItem("recentSearches");
        setRecentSearches([]);
        setShowRecentSearches(false);
    };

    const fetchSearchSuggestions = useCallback(
        async (searchTerm) => {
            if (
                !searchTerm ||
                searchTerm.length < 2 ||
                redirecting ||
                !searchActive
            ) {
                setSearchSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            setLoadingSuggestions(true);

            try {
                const response = await POST_REQUEST(API_ENDPOINTS["SEARCH"], {
                    lang: currentLang || "en",
                    name: searchTerm.trim(),
                    page: 1,
                    limit: 7,
                    search: true,
                });

                if (!redirecting && searchActive) {
                    setShowSuggestions(true);

                    if (
                        response?.list &&
                        Array.isArray(response.list) &&
                        response.list.length > 0
                    ) {
                        const validSuggestions = response.list.filter(
                            (item) => item && item.name && item.slug,
                        );
                        setSearchSuggestions(validSuggestions);
                    } else {
                        setSearchSuggestions([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching suggestions:", error);
                if (!redirecting && searchActive) {
                    setSearchSuggestions([]);
                    setShowSuggestions(true);
                }
            } finally {
                if (!redirecting && searchActive) {
                    setLoadingSuggestions(false);
                }
            }
        },
        [currentLang, redirecting, searchActive],
    );

    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (searchTerm) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                if (redirecting || !searchActive) {
                    return;
                }

                timeoutId = setTimeout(() => {
                    if (
                        !redirecting &&
                        searchActive &&
                        searchTerm &&
                        searchTerm.length >= 2
                    ) {
                        fetchSearchSuggestions(searchTerm);
                    }
                }, 300);
            };
        })(),
        [fetchSearchSuggestions, redirecting, searchActive],
    );

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        methods.setValue("search", value);

        if (value.length >= 2) {
            setShowRecentSearches(false);
            setShowSuggestions(true);
            debouncedSearch(value);
        } else {
            setShowSuggestions(false);
            if (recentSearches.length > 0) {
                setShowRecentSearches(true);
            }
        }
    };

    const handleSearchBlur = () => {
        setTimeout(() => {
            setShowSuggestions(false);
            setShowRecentSearches(false);
        }, 200);
    };

    const handleSuggestionClick = async (suggestion, url, push) => {
        if (redirecting) return;

        setRedirecting(true);
        saveRecentSearch(cleanSEOTags(suggestion.name));
        setShowSuggestions(false);
        setSearchActive(false);

        push(url);

        setTimeout(() => {
            setRedirecting(false);
        }, 1000);
    };

    const handleRecentSearchClick = async (searchTerm) => {
        if (redirecting) return;

        methods.setValue("search", searchTerm);
        saveRecentSearch(searchTerm);
        setShowRecentSearches(false);
        setShowSuggestions(true);
        fetchSearchSuggestions(searchTerm);
    };

    return (
        <header className="header_tech">
            <div className="offcanvas_backdrop"></div>
            <nav
                className={`navbar white__bg ${isScrolled ? "fixed-top" : ""}`}
            >
                <div className="custom-container">
                    <div className="navbar_wrap">
                        <a className="header_logo" href="/">
                            <img
                                className="img-fluid"
                                src={getImageURL(logo?.src)}
                                alt={logo?.alt || "Logo"}
                            />
                        </a>
                        <div className="nav_wrapper">
                            <div
                                className={`aside_menu ${
                                    menuOpen ? "active" : ""
                                }`}
                            >
                                <ul className={`menu`}>
                                    {menu?.map((item, index) => (
                                        <RenderMenu
                                            item={item}
                                            level={1}
                                            key={index}
                                            activeInactiveMenu={
                                                activeInactiveMenu
                                            }
                                            levelActive={levelActive}
                                        />
                                    ))}
                                    {button?.title && (
                                        <li className="menu_item contact-btn">
                                            <Button
                                                label={button?.title}
                                                href={button?.url}
                                                target={
                                                    button?.open_new_tab ===
                                                    "yes"
                                                        ? "_blank"
                                                        : undefined
                                                }
                                            />
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>

                        <div className="shop_menu nav__icon_menu lg">
                            <a className="stock__item" href="#!">
                                WAAREEENER <span>₹2,683.80</span>
                                <span className="icon_wrap">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={8}
                                        height={15}
                                        viewBox="0 0 8 15"
                                        fill="none"
                                    >
                                        <path
                                            d="M4.35355 0.646446C4.15829 0.451184 3.84171 0.451184 3.64645 0.646446L0.464466 3.82843C0.269203 4.02369 0.269203 4.34027 0.464466 4.53553C0.659728 4.7308 0.97631 4.7308 1.17157 4.53553L4 1.70711L6.82843 4.53553C7.02369 4.7308 7.34027 4.7308 7.53553 4.53553C7.7308 4.34027 7.7308 4.02369 7.53553 3.82843L4.35355 0.646446ZM4 15L4.5 15L4.5 1L4 1L3.5 1L3.5 15L4 15Z"
                                            fill="#0DB02B"
                                        />
                                    </svg>
                                </span>
                            </a>
                            <ul>
                                <li>
                                    <a
                                        href="#!"
                                        aria-label="Open search"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setSearchActive(!searchActive);
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width={26}
                                            height={26}
                                            viewBox="0 0 26 26"
                                            fill="none"
                                        >
                                            <path
                                                d="M11.6667 5C15.3485 5 18.3333 7.98477 18.3333 11.6667M19.2117 19.2065L25 25M22.3333 11.6667C22.3333 17.5577 17.5577 22.3333 11.6667 22.3333C5.77563 22.3333 1 17.5577 1 11.6667C1 5.77563 5.77563 1 11.6667 1C17.5577 1 22.3333 5.77563 22.3333 11.6667Z"
                                                stroke="white"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </a>
                                </li>
                                <li className="mobile_toggle_menu">
                                    <a
                                        className="nav_toggler"
                                        onClick={toggleMenu}
                                        role="button"
                                        aria-label="Open navigation menu"
                                        aria-expanded={menuOpen}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width={26}
                                            height={16}
                                            viewBox="0 0 26 16"
                                            fill="none"
                                        >
                                            <path
                                                d="M6 8L20 8"
                                                stroke="white"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M1 1H25"
                                                stroke="white"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M1 15H25"
                                                stroke="white"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <a className="nav_toggle" onClick={toggleMenu}>
                            <div className="bar">12</div>
                            <div className="bar"></div>
                            <div className="bar"></div>
                        </a>

                        <button
                            className={`nav_close ${menuOpen ? "active" : ""}`}
                            type="button"
                            onClick={toggleMenu}
                        >
                            <svg
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M16.3394 9.32245C16.7434 8.94589 16.7657 8.31312 16.3891 7.90911C16.0126 7.50509 15.3798 7.48283 14.9758 7.85938L12.0497 10.5866L9.32245 7.66048C8.94589 7.25647 8.31312 7.23421 7.90911 7.61076C7.50509 7.98731 7.48283 8.62008 7.85938 9.0241L10.5866 11.9502L7.66048 14.6775C7.25647 15.054 7.23421 15.6868 7.61076 16.0908C7.98731 16.4948 8.62008 16.5171 9.0241 16.1405L11.9502 13.4133L14.6775 16.3394C15.054 16.7434 15.6868 16.7657 16.0908 16.3891C16.4948 16.0126 16.5171 15.3798 16.1405 14.9758L13.4133 12.0497L16.3394 9.32245Z"
                                    fill="currentColor"
                                />
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12ZM12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <Fade show={searchActive} duration={0.2} unmountOnExit>
                    <div className="search-form-main active-search">
                        <div className="search-cross">
                            <button
                                type="button"
                                className="search-main search_btn"
                                onClick={() => setSearchActive(false)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width={45}
                                    height={45}
                                    viewBox="0 0 45 45"
                                    fill="none"
                                >
                                    <path
                                        d="M12.5438 30.5455C12.0166 31.0727 12.0166 31.9273 12.5438 32.4545C13.071 32.9818 13.9258 32.9818 14.453 32.4545L12.5438 30.5455ZM23.453 23.4545C23.9802 22.9273 23.9802 22.0727 23.453 21.5455C22.9258 21.0182 22.0711 21.0182 21.5439 21.5455L23.453 23.4545ZM21.5439 21.5455C21.0167 22.0727 21.0167 22.9273 21.5439 23.4545C22.0711 23.9818 22.9258 23.9818 23.453 23.4545L21.5439 21.5455ZM32.453 14.4546C32.9802 13.9274 32.9802 13.0726 32.453 12.5454C31.9258 12.0182 31.0711 12.0182 30.5439 12.5454L32.453 14.4546ZM23.453 21.5455C22.9258 21.0182 22.0711 21.0182 21.5439 21.5455C21.0167 22.0727 21.0167 22.9273 21.5439 23.4545C22.0711 23.9818 22.9258 23.9818 23.453 23.4545L21.5439 21.5455ZM30.5439 32.4545C31.0711 32.9818 31.9258 32.9818 32.453 32.4545C32.9802 31.9273 32.9802 31.0727 32.453 30.5455L30.5439 32.4545ZM21.5439 23.4545C22.0711 23.9818 22.9258 23.9818 23.453 23.4545C23.9802 22.9273 23.9802 22.0727 23.453 21.5455L21.5439 23.4545ZM14.453 12.5454C13.9258 12.0182 13.071 12.0182 12.5438 12.5454C12.0166 13.0726 12.0166 13.9274 12.5438 14.4546L14.453 12.5454ZM14.453 32.4545L23.453 23.4545L21.5439 21.5455L12.5438 30.5455L14.453 32.4545ZM23.453 23.4545L32.453 14.4546L30.5439 12.5454L21.5439 21.5455L23.453 23.4545ZM21.5439 23.4545L30.5439 32.4545L32.453 30.5455L23.453 21.5455L21.5439 23.4545ZM23.453 21.5455L14.453 12.5454L12.5438 14.4546L21.5439 23.4545L23.453 21.5455Z"
                                        fill="black"
                                    />
                                </svg>
                            </button>
                        </div>
                        <h3 className="heading fs-38 fw-300">
                            Find What You Need, Instantly!
                        </h3>
                        <div style={{ position: "relative" }}>
                            <form
                                onSubmit={(e) => e.preventDefault()}
                                className="search-form"
                            >
                                <input
                                    type="text"
                                    {...methods["register"]("search")}
                                    placeholder="Search ..."
                                    className="form-control"
                                    autoComplete="off"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                        }
                                    }}
                                    onChange={handleSearchInputChange}
                                    onBlur={handleSearchBlur}
                                    onFocus={() => {
                                        if (!methods.getValues("search")) {
                                            if (recentSearches.length > 0) {
                                                setShowRecentSearches(true);
                                            }
                                        } else if (
                                            methods.getValues("search")
                                                .length >= 2
                                        ) {
                                            setShowSuggestions(true);
                                        }
                                    }}
                                />
                            </form>

                            {showRecentSearches &&
                                recentSearches.length > 0 && (
                                    <div className="search-suggestions-dropdown">
                                        <div
                                            style={{
                                                padding: "8px 16px",
                                                borderBottom: "1px solid #eee",
                                                fontSize: "0.875rem",
                                                fontWeight: "600",
                                                color: "#666",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <span>Recent Searches</span>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    clearRecentSearches();
                                                }}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    color: "#999",
                                                    cursor: "pointer",
                                                    fontSize: "0.75rem",
                                                    padding: "2px 6px",
                                                    borderRadius: "3px",
                                                }}
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                        {recentSearches.map(
                                            (searchTerm, index) => (
                                                <div
                                                    key={index}
                                                    className="suggestion-item recent-search-item"
                                                    onMouseDown={(e) =>
                                                        e.preventDefault()
                                                    }
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        await handleRecentSearchClick(
                                                            searchTerm,
                                                        );
                                                    }}
                                                    style={{
                                                        padding: "12px 16px",
                                                        cursor: "pointer",
                                                        borderBottom:
                                                            index <
                                                            recentSearches.length -
                                                                1
                                                                ? "1px solid #f0f0f0"
                                                                : "none",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        style={{
                                                            color: "#999",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <path
                                                            d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                    <span
                                                        style={{
                                                            color: "#333",
                                                        }}
                                                    >
                                                        {searchTerm}
                                                    </span>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                )}

                            {showSuggestions &&
                                methods.getValues("search") &&
                                methods.getValues("search").length >= 2 && (
                                    <div className="search-suggestions-dropdown">
                                        {loadingSuggestions && (
                                            <div
                                                style={{
                                                    padding: "12px 16px",
                                                    textAlign: "center",
                                                    color: "#666",
                                                }}
                                            >
                                                Loading suggestions...
                                            </div>
                                        )}

                                        {!loadingSuggestions &&
                                            searchSuggestions.length === 0 && (
                                                <div
                                                    style={{
                                                        padding: "12px 16px",
                                                        textAlign: "center",
                                                        color: "#999",
                                                        fontStyle: "italic",
                                                    }}
                                                >
                                                    No data found
                                                </div>
                                            )}

                                        {!loadingSuggestions &&
                                            searchSuggestions.length > 0 &&
                                            searchSuggestions.map(
                                                (suggestion, index) => {
                                                    const isDocument =
                                                        !!suggestion.document;
                                                    const url = isDocument
                                                        ? getAbsoluteURL(
                                                              suggestion.document,
                                                          )
                                                        : suggestion.slug?.startsWith(
                                                                "/",
                                                            )
                                                          ? suggestion.slug
                                                          : `/${suggestion.slug}`;
                                                    return (
                                                        <Link
                                                            key={
                                                                suggestion.id ||
                                                                index
                                                            }
                                                            href={url}
                                                            target={
                                                                isDocument
                                                                    ? "_blank"
                                                                    : undefined
                                                            }
                                                            className="suggestion-item"
                                                            onMouseDown={(e) =>
                                                                e.preventDefault()
                                                            }
                                                            onClick={async (
                                                                e,
                                                            ) => {
                                                                if (
                                                                    isDocument
                                                                ) {
                                                                    saveRecentSearch(
                                                                        cleanSEOTags(
                                                                            suggestion.name,
                                                                        ),
                                                                    );
                                                                    setShowSuggestions(
                                                                        false,
                                                                    );
                                                                    setSearchActive(
                                                                        false,
                                                                    );
                                                                } else {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    await handleSuggestionClick(
                                                                        suggestion,
                                                                        url,
                                                                        router.push,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    justifyContent:
                                                                        "space-between",
                                                                    alignItems:
                                                                        "center",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight:
                                                                            "500",
                                                                        marginBottom:
                                                                            "4px",
                                                                    }}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: cleanSEOTags(
                                                                            suggestion.name,
                                                                        ),
                                                                    }}
                                                                />
                                                                {isDocument && (
                                                                    <svg
                                                                        width="20"
                                                                        height="20"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        style={{
                                                                            color: "#666",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                                        <polyline points="7 10 12 15 17 10"></polyline>
                                                                        <line
                                                                            x1="12"
                                                                            y1="15"
                                                                            x2="12"
                                                                            y2="3"
                                                                        ></line>
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            {suggestion.type && (
                                                                <span className="type-tag">
                                                                    {
                                                                        suggestion.type
                                                                    }
                                                                </span>
                                                            )}
                                                        </Link>
                                                    );
                                                },
                                            )}
                                    </div>
                                )}
                        </div>
                    </div>
                </Fade>
            </nav>
        </header>
    );
};

function RenderLevel1Comp({
    item,
    level,
    isActive,
    is1200,
    activeInactiveMenu,
    levelActive,
}) {
    return (
        <div
            className={`dropdown ${is1200 ? (isActive ? "mobile-active" : "") : ""}`}
        >
            <div className="dropdown_wrap">
                <div className="dropdown_nav">
                    <ul
                        className="mega_menu"
                        style={
                            item?.submenu?.length > 5
                                ? { overflowY: "auto" }
                                : {}
                        }
                    >
                        {item?.submenu?.map((subItem, subIndex) => (
                            <RenderMenu
                                item={subItem}
                                level={level + 1}
                                key={subIndex}
                                activeInactiveMenu={activeInactiveMenu}
                                levelActive={levelActive}
                            />
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

function RenderLevel2Comp({
    item,
    level,
    isActive,
    is1200,
    activeInactiveMenu,
    levelActive,
}) {
    return (
        <ul
            className={`dropdown submenu ${is1200 ? (isActive ? "mobile-active" : "") : ""}`}
        >
            {item?.submenu?.map((subItem, subIndex) => (
                <RenderMenu
                    item={subItem}
                    level={level + 1}
                    key={subIndex}
                    activeInactiveMenu={activeInactiveMenu}
                    levelActive={levelActive}
                />
            ))}
        </ul>
    );
}

const RenderMenu = ({
    item = {},
    level = 1,
    activeInactiveMenu,
    levelActive,
}) => {
    const [isActive, setIsActive] = useState(false);
    const { is1200, is1199 } = useCommonContext();

    if (!item?.link?.title && !item?.link?.url) {
        return null;
    }
    const validSubmenuItems = item?.submenu?.filter(
        (subItem) => subItem?.link?.title || subItem?.link?.url,
    );
    const hasSubmenu = !!validSubmenuItems?.length;

    return (
        <li
            className={`${level === 1 ? "menu_item" : ""} ${
                hasSubmenu ? "has-dropdown" : ""
            }`}
        >
            <a
                href={
                    item?.link?.url
                        ? item?.link?.url?.startsWith("/")
                            ? item?.link?.url
                            : `/${item?.link?.url}`
                        : "#"
                }
                onClick={(e) => {
                    if (is1200 && hasSubmenu) {
                        e.preventDefault();
                        activeInactiveMenu(level, item?.link?.title);
                    }

                    if (!item?.link?.url) {
                        e.preventDefault();
                    }
                    console.log(level, "level");
                }}
                style={{ cursor: "pointer" }}
            >
                {item?.link?.title}
                {hasSubmenu && (
                    <span
                        className="nav_icon"
                        style={{
                            ...(level > 1 &&
                                !is1200 && {
                                    transform: "rotate(270deg)",
                                }),
                            ...(is1200 && {
                                transform: `rotate(${
                                    levelActive &&
                                    levelActive[level] == item?.link?.title
                                        ? "180deg"
                                        : "0deg"
                                })`,
                            }),
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={10}
                            height={7}
                            viewBox="0 0 10 7"
                            fill="none"
                        >
                            <path
                                d="M1 1.5L5 5.5L9 1.5"
                                stroke="#5B5B5B"
                                strokeWidth={2}
                            />
                        </svg>
                    </span>
                )}
            </a>

            {hasSubmenu && level === 1 ? (
                is1200 ? (
                    <Collapse
                        show={
                            levelActive &&
                            levelActive[level] == item?.link?.title
                                ? true
                                : false
                        }
                    >
                        <RenderLevel1Comp
                            item={item}
                            level={level}
                            isActive={isActive}
                            is1200={is1200}
                            activeInactiveMenu={activeInactiveMenu}
                            levelActive={levelActive}
                        />
                    </Collapse>
                ) : (
                    <RenderLevel1Comp
                        item={item}
                        level={level}
                        isActive={isActive}
                        is1200={is1200}
                        activeInactiveMenu={activeInactiveMenu}
                        levelActive={levelActive}
                    />
                )
            ) : hasSubmenu ? (
                is1200 ? (
                    <Collapse
                        show={
                            levelActive &&
                            levelActive[level] == item?.link?.title
                                ? true
                                : false
                        }
                    >
                        <RenderLevel2Comp
                            item={item}
                            level={level}
                            isActive={isActive}
                            is1200={is1200}
                            activeInactiveMenu={activeInactiveMenu}
                            levelActive={levelActive}
                        />
                    </Collapse>
                ) : (
                    <RenderLevel2Comp
                        item={item}
                        level={level}
                        isActive={isActive}
                        is1200={is1200}
                        activeInactiveMenu={activeInactiveMenu}
                        levelActive={levelActive}
                    />
                )
            ) : null}
        </li>
    );
};
